import React from 'react';
import {
  AuxButton,
  IconContainer,
  ProjectTag,
  StyledLabel,
  StyledListContainer,
  StyledListItem,
} from '../Shared/Styled';
import '../../utils/scrollIntoViewIfNeeded';
import { Colors } from 'msteams-ui-styles-core';
import { ThemeString, useThemeContext } from '../../context';
import { defaultFont, separatorColor } from '../../styles/SharedStyles';
import { SelectItem } from '../../types';

const additionalTextStyles: React.CSSProperties = {
  fontWeight: 'bold',
};

export interface SelectListProps {
  label?: string;
  selected?: string;
  items: SelectItem[];
  selectedProperty?: string;
}

export function SelectList({
  label,
  selected,
  items,
  selectedProperty,
}: SelectListProps): JSX.Element {
  const selectedProp = selectedProperty || 'text';
  const theme = useThemeContext();

  return (
    <StyledListContainer>
      <Label text={label} theme={theme} />
      <List
        items={items}
        theme={theme}
        selected={selected}
        selectedProp={selectedProp}
      />
    </StyledListContainer>
  );
}

interface LabelProps {
  text?: string;
  theme: any;
}

export function Label({ text, theme }: LabelProps): JSX.Element | null {
  if (!text) {
    return null;
  }
  return (
    <StyledLabel themeStr={theme}>
      <span>{text}</span>
    </StyledLabel>
  );
}

interface ListProps {
  items: SelectItem[];
  theme: ThemeString;
  selected?: string;
  selectedProp: string;
}

function List({
  items,
  theme,
  selected,
  selectedProp,
}: ListProps): JSX.Element {
  return (
    <StyledList>
      {items.map((item, index) => {
        return (
          <ListItem
            key={index}
            index={index}
            item={item}
            theme={theme}
            selected={selected === item[selectedProp]}
          />
        );
      })}
    </StyledList>
  );
}

interface StyledListProps {
  children: any;
}

function StyledList({ children }: StyledListProps): JSX.Element {
  const theme = useThemeContext();
  return (
    <ul
      style={{
        ...defaultFont,
        paddingLeft: '0',
        margin: '0',
        overflowY: 'auto',
        border: '1px solid',
        borderColor: separatorColor(theme),
        borderTop: '0',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      {children}
    </ul>
  );
}

interface ListItemProps {
  item: SelectItem;
  theme: ThemeString;
  selected: boolean;
  index: number;
}

function ListItem({
  item,
  theme,
  selected,
  index,
}: ListItemProps): JSX.Element {
  return (
    <StyledListItem
      item={item}
      onClick={item.onClick}
      index={index}
      selected={selected}
      ref={el => selected && el && el.scrollIntoViewIfNeeded()}
      themeStr={theme}
      hasBorder={!!item.additionalText}
      data-testid={`select_list_item_${index}`}
    >
      {item.icon && (
        <ItemIcon icon={item.icon} theme={theme} selected={selected} />
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: !!item.date ? 'row' : 'column',
          width: '100%',
          wordWrap: 'break-word',
          justifyContent: !!item.date ? 'space-between' : 'initial',
        }}
        title={item.title}
      >
        {item.additionalText && (
          <p style={additionalTextStyles}>
            {item.additionalText}
            {item.isPw365Project && <ProjectTag type='pw365'>PW365</ProjectTag>}
            {item.external && <ProjectTag>External</ProjectTag>}
          </p>
        )}

        <p>{item.text}</p>
        {item.date && <p>{item.date}</p>}
      </div>
      {item.rightButton && (
        <RightButton
          buttonProperties={item.rightButton}
          itemSelected={selected}
        />
      )}
    </StyledListItem>
  );
}

interface ItemIconProps {
  icon: any;
  theme: any;
  selected: boolean;
  style?: any;
}

export function ItemIcon({ icon, theme, selected, style }: any): JSX.Element {
  const replaceColor =
    theme === 'dark' ? 'white' : selected ? 'white' : Colors.light.brand00;

  const hasFill = icon.match('fill=');
  if (!hasFill) {
    icon = icon.replace(/\<path/g, `<path fill='${replaceColor}'`);
  }
  return (
    <IconContainer
      themeStr={theme}
      dangerouslySetInnerHTML={{
        __html: icon.replace(/#000+/g, replaceColor),
      }}
      style={style}
    />
  );
}

function RightButton({
  buttonProperties,
  itemSelected,
}: any): JSX.Element | null {
  if (buttonProperties.text === 'Default for Team' || itemSelected) {
    return (
      <AuxButton
        style={{
          alignSelf: '',
        }}
        onClick={buttonProperties.onClick}
      >
        {buttonProperties.text}
      </AuxButton>
    );
  }
  return null;
}
